import { Injectable, Optional, Inject, OnDestroy } from '@angular/core';
import { Observable, Subject, from, of, throwError, timer } from 'rxjs';
import {
   catchError,
   map,
   takeUntil,
   finalize,
   share,
   timeout,
   retry,
   delay,
} from 'rxjs/operators';

// Enums
export enum TikTokEventType {
   VIEW_CONTENT = 'ViewContent',
   INITIATE_CHECKOUT = 'InitiateCheckout',
   COMPLETE_REGISTRATION = 'CompleteRegistration',
   COMPLETE_PAYMENT = 'CompletePayment',
}

export enum TikTokContentType {
   PRODUCT = 'product',
   PRODUCT_GROUP = 'product_group',
}

// Interfaces
export interface TikTokConfig {
   readonly enabled: boolean;
   readonly currency: string;
   readonly debugMode?: boolean;
}

export interface TikTokEventParams {
   readonly eventName: TikTokEventType;
   readonly value: number;
   readonly contents: ReadonlyArray<TikTokEventContentItem>;
   readonly eventID?: string;
}

export interface TikTokEventContentItem {
   readonly contentID?: string;
   readonly contentType: TikTokContentType;
   readonly contentName: string;
   readonly quantity: number;
   readonly price: number;
}

export interface TikTokIdentifyParams {
   readonly email: string;
   readonly externalID: string;
   readonly phoneNumber?: string;
}

// Token para inyección de configuración
export const TIKTOK_CONFIG = 'TIKTOK_CONFIG';

// Interfaz para ttq global
declare interface TTQ {
   identify(params: {
      email?: string;
      phone_number?: string;
      external_id?: string;
   }): void;
   track(eventName: string, params: any): void;
}

declare var ttq: TTQ | undefined;

@Injectable({
   providedIn: 'root',
})
export class TikTokService implements OnDestroy {
   private readonly HASH_ALGORITHM = 'SHA-256';
   private readonly OPERATION_TIMEOUT = 5000; // 5 segundos
   private readonly MAX_RETRIES = 2;
   private readonly CLEANUP_INTERVAL = 300000; // 5 minutos

   private destroy$ = new Subject<void>();
   private identifyOperationsCache = new Map<string, Observable<void>>();

   constructor(
      @Optional()
      @Inject(TIKTOK_CONFIG)
      private config: TikTokConfig = {
         enabled: false,
         currency: 'MXN',
      },
      @Optional() @Inject('Console') private logger: Console = console
   ) {
      this.setupCacheCleanup();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
      this.identifyOperationsCache.clear();
   }

   /**
    * Verifica si el servicio de TikTok está disponible
    */
   private get isAvailable(): boolean {
      return this.config.enabled && typeof ttq !== 'undefined';
   }

   /**
    * Identifica al usuario enviando datos PII hashados
    */
   identify(params: TikTokIdentifyParams): Observable<void> {
      if (!this.isAvailable) {
         return this.handleServiceUnavailable();
      }

      const cacheKey = this.createCacheKey(params);
      const cachedOperation = this.identifyOperationsCache.get(cacheKey);

      if (cachedOperation) {
         return cachedOperation;
      }

      const operation = from(this.prepareIdentifyData(params)).pipe(
         retry(this.MAX_RETRIES),
         timeout(this.OPERATION_TIMEOUT),
         map((hashedData) => {
            ttq!.identify(hashedData);
         }),
         share(),
         catchError(this.handleError),
         finalize(() => {
            this.identifyOperationsCache.delete(cacheKey);
         }),
         takeUntil(this.destroy$)
      );

      this.identifyOperationsCache.set(cacheKey, operation);
      return operation;
   }

   /**
    * Realiza el seguimiento de un evento de TikTok
    */
   track(
      eventName: TikTokEventType,
      params: Omit<TikTokEventParams, 'eventName'>
   ): Observable<void> {
      if (!this.isAvailable) {
         return this.handleServiceUnavailable();
      }

      try {
         // Validar que haya contents
         if (!params.contents || params.contents.length === 0) {
            console.log('TikTok track: contents no puede estar vacío');
         }

         return of(void 0).pipe(
            map(() => {
               ttq!.track(eventName, {
                  contents: this.validateContents(params.contents),
                  value: this.validateNumericValue(params.value),
                  currency: this.config.currency,
                  event_id: params.eventID,
               });
            }),
            retry(this.MAX_RETRIES),
            timeout(this.OPERATION_TIMEOUT),
            catchError(this.handleError),
            takeUntil(this.destroy$)
         );
      } catch (error) {
         console.log('Error al rastrear evento TikTok:', error);
         return throwError(() => error);
      }
   }

   /**
    * Métodos específicos para cada tipo de evento
    */
   viewContent(
      params: Omit<TikTokEventParams, 'eventName' | 'eventID'>
   ): Observable<void> {
      return this.track(TikTokEventType.VIEW_CONTENT, params);
   }

   initiateCheckout(
      params: Omit<TikTokEventParams, 'eventName' | 'eventID'>
   ): Observable<void> {
      return this.track(TikTokEventType.INITIATE_CHECKOUT, params);
   }

   completeRegistration(
      params: Omit<TikTokEventParams, 'eventName' | 'eventID'>
   ): Observable<void> {
      return this.track(TikTokEventType.COMPLETE_REGISTRATION, params);
   }

   completePayment(
      params: Omit<TikTokEventParams, 'eventName' | 'eventID'>
   ): Observable<void> {
      return this.track(TikTokEventType.COMPLETE_PAYMENT, params);
   }

   /**
    * Configuración de limpieza periódica del cache
    */
   private setupCacheCleanup(): void {
      timer(this.CLEANUP_INTERVAL, this.CLEANUP_INTERVAL)
         .pipe(
            takeUntil(this.destroy$),
            catchError((error) => {
               return of(null).pipe(delay(this.CLEANUP_INTERVAL));
            })
         )
         .subscribe(() => {
            this.cleanupCache();
            this.logDebug(
               `Cache limpiado. Entradas: ${this.identifyOperationsCache.size}`
            );
         });
   }

   /**
    * Métodos privados auxiliares
    */
   private async prepareIdentifyData(params: TikTokIdentifyParams): Promise<{
      email: string;
      external_id: string;
      phone_number?: string;
   }> {
      const hashedData = {
         email: await this.hashData(params.email.toLowerCase().trim()),
         external_id: await this.hashData(params.externalID),
      };

      if (params.phoneNumber) {
         return {
            ...hashedData,
            phone_number: await this.hashData(
               this.normalizePhoneNumber(params.phoneNumber)
            ),
         };
      }

      return hashedData;
   }

   private validateContents(
      contents: ReadonlyArray<TikTokEventContentItem>
   ): Array<any> {
      return contents.map((content) => {
         // Validar que contentID no esté vacío
         if (!content.contentID || content.contentID.trim() === '') {
            console.log('content_id es requerido para Video Shopping Ads (VSA)');
         }

         // Transformar al formato esperado por TikTok
         return {
            content_id: content.contentID.trim(), // Asegurar que se envía como content_id
            content_type: content.contentType,
            content_name: content.contentName,
            quantity: this.validateNumericValue(content.quantity),
            price: this.validateNumericValue(content.price),
         };
      });
   }

   private validateNumericValue(value: number): number {
      if (typeof value !== 'number' || isNaN(value) || value < 0) {
         throw new Error(`Valor inválido: ${value}`);
      }
      return value;
   }

   private normalizePhoneNumber(phone: string): string {
      return phone.replace(/\D/g, '');
   }

   private async hashData(data: string): Promise<string> {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      const hashBuffer = await crypto.subtle.digest(
         this.HASH_ALGORITHM,
         dataBuffer
      );
      return this.bufferToHex(hashBuffer);
   }

   private bufferToHex(buffer: ArrayBuffer): string {
      return Array.from(new Uint8Array(buffer))
         .map((b) => b.toString(16).padStart(2, '0'))
         .join('');
   }

   private cleanupCache(): void {
      this.identifyOperationsCache.clear();
   }

   private createCacheKey(params: TikTokIdentifyParams): string {
      return `${params.email}-${params.externalID}-${params.phoneNumber || ''}`;
   }

   private handleError = (error: any): Observable<never> => {
      //this.logError('Error en operación de TikTok:', error);
      return throwError(() => new Error('Operación fallida'));
   };

   private handleServiceUnavailable(): Observable<void> {
      //this.logWarning('TikTok Pixel no está disponible.');
      return of(void 0);
   }

   /* Métodos de logging seguros */
   private logError(...args: any[]): void {
     if (typeof this.logger?.error !== 'function') {
       return;
     }
     try {
       //this.logger?.error('[TikTok Service]', ...args);
     } catch (_e) {
     }
   }
   
   private logWarning(...args: any[]): void {
     if (!this.config?.debugMode || typeof this.logger?.warn !== 'function') {
       return;
     }
     try {
       //this.logger?.warn('[TikTok Service]', ...args);
     } catch (_e) {
     }
   }
   
   private logDebug(...args: any[]): void {
     if (!this.config?.debugMode || typeof this.logger?.debug !== 'function') {
       return;
     }
     try {
       //this.logger?.debug('[TikTok Service]', ...args);
     } catch (_e) {
     }
   }
}
